import { useCallback, useState } from "react";
import type { HaulierCreatePayload } from "@/customTypings/haulier/Haulier";
import type { HaulierDeletedRestoreAvailableConflict } from "@/utils/Haulier/haulierCreateConflict";
import {
    HAULIER_CREATE_CONFLICT,
    HaulierCreateConflictError,
    isHaulierDeletedRestoreAvailableConflict,
} from "@/utils/Haulier/haulierCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type useHaulierRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    restoreDeletedWithCreateValues: (payload: HaulierCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
};

export function useHaulierRestoreOnCreateConflict<TDetail extends { id: string }>({
    restoreDeletedWithCreateValues,
    onRestored,
}: useHaulierRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<HaulierDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<HaulierCreatePayload | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback((err: unknown): boolean => {
        if (err instanceof HaulierCreateConflictError) {
            if (
                isHaulierDeletedRestoreAvailableConflict(
                    err.conflict,
                    HAULIER_CREATE_CONFLICT.deletedRestoreAvailable
                )
            ) {
                setRestoreConflict(err.conflict);
                return true;
            }
        }
        return false;
    }, []);

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    ...pendingRestoreForm,
                });
                const label = restoreConflict.existingName;
                clearRestoreConflict();
                showToastSuccess(`"${label}" restored successfully!`);
                onRestored(restored);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [restoreConflict, pendingRestoreForm, restoreDeletedWithCreateValues, onRestored, clearRestoreConflict]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: "Restore deleted Haulier?",
        body: restoreConflict
            ? "A deleted Haulier matching these details already exists. Restore it using the details you entered?"
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        handleCreateError,
        setPendingRestoreForm,
        confirmRestore,
        restoreModalProps,
    };
}
